import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home, ArrowRight } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

// Branded 404 for unknown routes. Explicitly noindex so nonexistent URLs never
// get indexed, and we never silently render the homepage for a bad URL.
export default function NotFound() {
  usePageMeta({ title: "Page Not Found | Time Furniture", description: "The page you are looking for could not be found.", noindex: true });

  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    const had = !!m; const prev = m ? m.getAttribute("content") : null;
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "robots"); document.head.appendChild(m); }
    m.setAttribute("content", "noindex, follow");
    // Remove the canonical (usePageMeta sets one) so a 404 gives one clear
    // signal and never canonicalizes to a real URL. Restored on unmount.
    const canon = document.head.querySelector('link[rel="canonical"]');
    const canonParent = canon ? canon.parentNode : null;
    const canonNext = canon ? canon.nextSibling : null;
    if (canon) canon.remove();
    return () => {
      if (had && prev != null) m.setAttribute("content", prev); else if (m && m.parentNode) m.parentNode.removeChild(m);
      if (canon && canonParent) canonParent.insertBefore(canon, canonNext);
    };
  }, []);

  return (
    <div data-testid="not-found-page" className="bg-[#FAF5EC] min-h-[70vh] flex items-center">
      <div className="max-w-[820px] mx-auto px-6 md:px-12 py-24 text-center">
        <p className="eyebrow mb-5">Error 404</p>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-[#1C110C]">
          This page doesn't exist.
        </h1>
        <p className="mt-6 max-w-xl mx-auto text-[#7A6B5F] leading-relaxed">
          The link may be broken or the page may have moved. Let's get you back to something useful.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/" data-testid="notfound-home" className="inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-9 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]">
            <Home size={15} /> Back to home
          </Link>
          <Link to="/portfolio" data-testid="notfound-portfolio" className="inline-flex items-center gap-3 border border-[#6E1F2C]/40 text-[#6E1F2C] px-9 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white">
            View projects <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}
